import { Button, IconButton } from "~/components/reusable/Button"
import { Copy, Warning } from "@phosphor-icons/react"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useIntercom } from "react-use-intercom"

interface GenericErrorProps {
  error?: Error
  message?: string
}

const GenericError = ({ error, message = "An unexpected error occurred" }: GenericErrorProps) => {
  const { show: showIntercom } = useIntercom()

  const displayMessage = message || error?.message || "An unexpected error occurred"

  const fullErrorObject = {
    message: error?.message || message,
    name: error?.name,
    stack: error?.stack,
    cause: error?.cause,
    ...(error && typeof error === "object"
      ? Object.fromEntries(
          Object.entries(error).filter(
            ([key]) => !["message", "name", "stack", "cause"].includes(key),
          ),
        )
      : {}),
  }

  const handleCopyFullError = () => {
    const errorString = JSON.stringify(fullErrorObject, null, 2)
    navigator.clipboard.writeText(errorString)
  }

  return (
    <div className="flex h-viewport-height items-center justify-center bg-subtle">
      <div className="flex flex-col rounded-[16px] shadow-modal bg-base w-[550px]">
        <div className="flex flex-col gap-5 p-5">
          <div className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-red-600">
            <Warning size={20} className="text-white" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="label-lg">Something went wrong</span>
            <p className="paragraph-base text-fg-subtle">
              An error happened because of the following:
            </p>
          </div>
          <div className="bg-subtle rounded-[12px] border border-base p-4 max-h-[150px] overflow-y-auto relative">
            <div className="absolute right-3 top-2.5 flex gap-2 z-10">
              <Tooltip title="Copy error to clipboard">
                <IconButton
                  onClick={handleCopyFullError}
                  title="Copy error to clipboard"
                  variant="neutral"
                >
                  <Copy size={16} />
                </IconButton>
              </Tooltip>
            </div>
            <p className="paragraph-base text-fg-subtle whitespace-pre-wrap break-words text-left">
              {displayMessage}
            </p>
          </div>
        </div>
        <div className="flex flex-row gap-3 items-center justify-between bg-subtle px-5 py-4 rounded-b-[16px] border-t border-base">
          <Button onClick={() => showIntercom()} variant="transparent">
            Report Problem
          </Button>
          <div className="flex justify-end w-full gap-3">
            <Button onClick={() => window.location.reload()} variant="neutral">
              Try Again
            </Button>
            <Button onClick={() => (window.location.href = "/")}>Back to Home</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GenericError
